import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-md border border-line bg-bg-elevated px-4 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 transition";

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-ink-primary relative overflow-hidden">
      <div className="absolute inset-0 scan-line opacity-50" />
      <div className="absolute top-0 left-0 right-0 p-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-ink-secondary hover:text-ink-primary transition" data-testid="back-to-login">
          <ArrowLeft className="h-4 w-4" />
          Back to sign in
        </Link>
      </div>

      <div className="relative w-full max-w-md p-8">
        <div className="rounded-2xl border border-line bg-bg-surface p-8 lift">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-3">
            // PASSWORD RESET
          </div>

          {done ? (
            <div data-testid="reset-success">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                <h1 className="font-display text-2xl font-black tracking-tighter">Password updated</h1>
              </div>
              <p className="text-sm text-ink-secondary mb-6">
                Your new password is set. Sign in with it now.
              </p>
              <Link
                to="/login"
                data-testid="reset-goto-login"
                className="block w-full text-center rounded-md bg-brand-amber px-6 py-2.5 text-black font-semibold text-sm hover:bg-brand-amber/90 transition"
              >
                Go to sign in
              </Link>
            </div>
          ) : !token ? (
            <div data-testid="reset-no-token">
              <h1 className="font-display text-2xl font-black tracking-tighter mb-2">Invalid reset link</h1>
              <p className="text-sm text-ink-secondary mb-6">
                This link is missing its reset token. Request a new one from the sign-in page.
              </p>
              <Link to="/login" className="text-brand-amber text-sm font-medium hover:text-brand-amber/80 transition">
                ← Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-black tracking-tighter mb-2">Choose a new password</h1>
              <p className="text-sm text-ink-secondary mb-6">
                Minimum 8 characters. You'll be signed out of all other devices.
              </p>

              {error && (
                <div className="mb-5 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="reset-error-banner">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-3" data-testid="reset-password-form">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className={inputCls}
                  data-testid="reset-password-input"
                />
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className={inputCls}
                  data-testid="reset-confirm-input"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="reset-submit-btn"
                  className="w-full flex items-center justify-center gap-2 rounded-md bg-brand-amber px-6 py-2.5 text-black font-semibold text-sm hover:bg-brand-amber/90 disabled:opacity-60 transition"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  Set new password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
